import { useRouter } from "next/router";
import { validateEmailAddress } from "@webmonitoring/vp-common/Utils/StringValidators";
import classNames from "classnames";
import { useCallback, useRef, useState } from "react";
import "react-image-crop/dist/ReactCrop.css";
import Button from "@/components/UI/Button/Button";
import Card, { CardProps } from "@/components/UI/Card/Card";
import TextField from "@/components/UI/TextField/TextField";
import { createJob } from "@/utils/api/job-service/jobs";
import ScreenshotErrorState from "./ScreenshotErrorState";
// @ts-ignore
import { Crop, ReactCrop } from "react-image-crop";
import { getAdCampaign, getReferrer } from "@/utils/storage";
import Image from "next/image";
import ScreenshotLoadingState from "./ScreenshotLoadingState";
import ScreenshotEmptyState from "./ScreenshotEmptyState";
import { createFastshot } from "@/utils/api/screenshot-service/fastshot";
import { track } from "@/track";
import { toPercentCrop, toPixelsCrop, toSerializedCrop, toUnserializedCrop } from "@/utils/job";
import { publicConfig } from "@/PublicConfig";
import { JobCreatorConfig, defaultCropDimensions, toJobCreatorConfigFromFastshot } from "./utils";
import { validateUrl } from "@/utils/url";
import { isServiceError } from "@/utils/error";
import Select from "@/components/UI/Select/Select";
import { toSelectOptions } from "@/utils/cms";
import { createEmailCheck } from "@/utils/api/account-service/user-exists";

// See: https://www.notion.so/visualping/Section01-3173e3a372234424aa1a748844b13b29

interface JobCreatorProps extends CardProps {
  cropDisabled?: boolean;
  defaultImageError?: string;
  defaultJobCreatorConfig?: JobCreatorConfig;
  page: any;
  section: any;
}

const JobCreator = ({
  className,
  cropDisabled,
  defaultImageError,
  defaultJobCreatorConfig,
  page,
  section,
  ...props
}: JobCreatorProps) => {
  const router = useRouter();

  const cardRef = useRef<HTMLDivElement>(null);
  const fastshotBoxRef = useRef<HTMLDivElement>(null);

  const [fastshotLoading, setFastshotLoading] = useState(false);
  const [createAccountLoading, setCreateAccountLoading] = useState(false);

  // Initial crop unit must be percent and change to pixel on crop change.
  const [cropUnit, setCropUnit] = useState<Crop["unit"]>("%");

  const [jobCreatorConfig, setJobCreatorConfig] = useState({
    email: "",
    interval: section?.IntervalSelect?.OptionDefaultValue ?? "1440",
    mode: "VISUAL",
    notification_threshold: 1,
    trigger: "1",
    url: "",
    wait_time: 2,
    ...defaultJobCreatorConfig,
  });

  const [urlError, setUrlError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [imageError, setImageError] = useState(defaultImageError ?? "");

  const onImageError = useCallback(() => setImageError("error"), []);

  return (
    <Card className={classNames("flex flex-col gap-5 p-5", className)} rounded="large" ref={cardRef} {...props}>
      <form
        className="flex gap-2"
        onSubmit={async (event) => {
          event.preventDefault();

          track.signUp.jobSearchStart({
            url: jobCreatorConfig?.url,
          });

          setUrlError("");

          const urlValidationResult = validateUrl(jobCreatorConfig?.url ?? "")?.[0];

          if (urlValidationResult?.length) {
            track.signUp.jobSearchURLValidation({
              error: "validationFailed",
              url: jobCreatorConfig?.url,
            });

            if (urlValidationResult === "nonStandardProtocol") {
              setUrlError(section?.UrlProtocolErrorMessage);
            } else if (urlValidationResult === "malformedURL") {
              setUrlError(section?.UrlMalformedErrorMessage);
            } else if (urlValidationResult === "urlTooLong") {
              setUrlError(section?.UrlTooLongErrorMessage);
            }

            return;
          }

          setTimeout(() => {
            const rect = cardRef.current?.getBoundingClientRect();

            if (rect) {
              const offset = 24;

              window.scrollTo({
                behavior: "smooth",
                top: window.scrollY + rect.y - offset,
              });
            }
          }, 100);

          setImageError("");
          setFastshotLoading(true);

          let createFastshotResponse: Awaited<ReturnType<typeof createFastshot>>;

          try {
            createFastshotResponse = await createFastshot({
              payload: {
                getAllText: false,
                getHTMLTree: jobCreatorConfig?.mode !== "VISUAL",
                preactions: { active: false, actions: [] },
                target_device: "4",
                url: jobCreatorConfig?.url,
                wait_time: "2",
              },
            });
          } catch (error) {
            setImageError("error");

            if (isServiceError(error)) {
              if (error.code === "BAD_ARGUMENTS_ERROR") {
                setUrlError(section?.UrlMalformedErrorMessage);
              } else if (error.code === "BAD_URL_ERROR") {
                setUrlError(section?.UrlNotFoundErrorMessage);
              } else if (error.status === 500 && error.message.includes("NOT_RESOLVED")) {
                setUrlError(section?.UrlDnsErrorMessage);
              } else if (error.status === 403 && error.code === "SECURITY_ERROR") {
                setUrlError(section?.UrlInvalidErrorMessage);
              } else {
                setUrlError(section?.UrlInternalErrorMessage);
              }
            } else {
              setUrlError(section?.UrlInternalErrorMessage);
            }

            return;
          } finally {
            setFastshotLoading(false);
          }

          setCropUnit("%");

          setJobCreatorConfig((previous) => {
            return {
              ...previous,
              crop_dimensions: defaultCropDimensions,
              ...toJobCreatorConfigFromFastshot({
                response: createFastshotResponse,
              }),
            };
          });
        }}
      >
        <TextField
          aria-label="Job url"
          className="flex-1"
          disabled={section?.UrlTextField?.Disabled || fastshotLoading}
          error={Boolean(urlError)}
          errorMessage={urlError}
          errorTooltipProps={{
            color: "error",
            show: true,
          }}
          id={`${section.id}-url-text-field`}
          name="url-text-field"
          onChange={(event) => {
            setUrlError("");
            setJobCreatorConfig((previous) => {
              return {
                ...previous,
                url: event.target.value,
              };
            });
          }}
          placeholder={section?.UrlTextField?.Placeholder}
          size="large"
          type="text"
          value={jobCreatorConfig?.url}
        />
        {section?.UrlSubmitButton && (
          <Button
            className="w-full max-w-[150px]"
            disabled={section?.UrlSubmitButton?.Disabled || fastshotLoading || !jobCreatorConfig?.url}
            loading={fastshotLoading}
          >
            {section?.UrlSubmitButton?.Value}
          </Button>
        )}
      </form>
      <div className="h-[467px] overflow-y-scroll rounded-md border bg-vpGray-300" ref={fastshotBoxRef}>
        {imageError ? (
          <ScreenshotErrorState className="h-full" screenshotUrl={jobCreatorConfig?.url} section={section} />
        ) : fastshotLoading ? (
          <ScreenshotLoadingState className="h-full" />
        ) : !jobCreatorConfig?.image_uncropped ? (
          <ScreenshotEmptyState className="h-full" section={section} />
        ) : (
          <ReactCrop
            crop={
              cropUnit === "%"
                ? toPercentCrop({
                    crop: jobCreatorConfig?.crop_dimensions,
                    target: {
                      height: jobCreatorConfig?.image_uncropped_height ?? 0,
                      width: jobCreatorConfig?.image_uncropped_width ?? 0,
                    },
                  })
                : toPixelsCrop({
                    crop: jobCreatorConfig?.crop_dimensions,
                  })
            }
            disabled={cropDisabled}
            keepSelection
            onChange={(crop: any) => {
              setCropUnit("px");

              setJobCreatorConfig((previous) => {
                return {
                  ...previous,
                  crop_dimensions: toSerializedCrop({ crop }),
                };
              });
            }}
          >
            <Image
              alt="Latest website image capture"
              height={jobCreatorConfig.image_uncropped_height}
              onError={onImageError}
              onLoad={(event) => {
                const percentCrop = toPercentCrop({
                  crop: jobCreatorConfig?.crop_dimensions,
                  target: {
                    height: jobCreatorConfig?.image_uncropped_height ?? 0,
                    width: jobCreatorConfig?.image_uncropped_width ?? 0,
                  },
                });

                const fastshotBoxMargin = 100;
                const fastshotBoxHeight = fastshotBoxRef.current?.getBoundingClientRect().height ?? 0;

                const cropY = event.currentTarget.height * (percentCrop.y! / 100);
                const intersectY = fastshotBoxHeight - fastshotBoxMargin;

                const viewportPadding = 50;

                const nextScrollTop = cropY >= intersectY ? cropY - viewportPadding : 0;

                fastshotBoxRef.current?.scrollTo({
                  behavior: "smooth",
                  top: nextScrollTop,
                });
              }}
              priority
              src={jobCreatorConfig.image_uncropped}
              width={jobCreatorConfig.image_uncropped_width}
            />
          </ReactCrop>
        )}
      </div>
      <div>
        <form
          className="flex flex-col gap-5"
          onSubmit={async (event) => {
            event.preventDefault();

            const validateEmailAddressResult = validateEmailAddress(jobCreatorConfig?.email ?? "");

            if (validateEmailAddressResult === "malformed") {
              setEmailError(section.EmailDefaultErrorMessage);
              return;
            }

            setCreateAccountLoading(true);

            try {
              const response = await createEmailCheck({
                payload: {
                  email: jobCreatorConfig?.email ?? "",
                },
              });

              if (response.status !== "no") {
                if (response.status === "active") {
                  setEmailError(section.CreateAccountEmailExistsErrorMessage);
                } else {
                  setEmailError(section.CreateAccountDefaultErrorMessage);
                }

                setCreateAccountLoading(false);

                return;
              }
            } catch (error) {
              setCreateAccountLoading(false);
              setEmailError(section.CreateAccountDefaultErrorMessage);
              return;
            }

            setEmailError("");

            let createJobResponse;

            try {
              createJobResponse = await createJob({
                payload: {
                  active: true,
                  ad_campaign: getAdCampaign(),
                  crop: toUnserializedCrop({
                    crop: jobCreatorConfig?.crop_dimensions,
                  }),
                  description: jobCreatorConfig?.description,
                  email: jobCreatorConfig?.email,
                  interval: jobCreatorConfig?.interval,
                  keyword_action: jobCreatorConfig?.keyword_action,
                  keywords: jobCreatorConfig?.keywords,
                  locale: page?.attributes?.locale,
                  mode: jobCreatorConfig?.mode,
                  notification_threshold: jobCreatorConfig?.notification_threshold,
                  proxy_id: jobCreatorConfig?.proxy_id,
                  siteReferer: getReferrer(),
                  target_device: "4",
                  trigger: jobCreatorConfig?.trigger,
                  url: jobCreatorConfig?.url,
                  wait_time: jobCreatorConfig?.wait_time,
                  xpath: jobCreatorConfig?.xpath,
                },
              });
            } catch (_error) {
              setCreateAccountLoading(false);
              setEmailError(section?.CreateAccountDefaultErrorMessage);
              return;
            }

            track.signUp.signUpStart({
              actions: [],
              compare_type: jobCreatorConfig?.mode,
              frequency: String(jobCreatorConfig?.interval),
              URL: jobCreatorConfig?.url,
            });

            const mainAppRootUrl = new URL(publicConfig.visualpingWebURL);

            mainAppRootUrl.pathname =
              page?.attributes?.locale === "en" ? "/sign-up" : `${page?.attributes?.locale}/sign-up`;

            mainAppRootUrl.searchParams.set("email", createJobResponse?.email ?? "");

            window.location.href = mainAppRootUrl.toString();
          }}
        >
          <div className="flex flex-col gap-2 lg:flex-row">
            {section?.EmailTextField && (
              <div className="flex flex-1 flex-col gap-2">
                <label htmlFor="email" className="text-gray-700">
                  {section?.EmailTextField.Label}
                </label>
                <TextField
                  error={Boolean(emailError)}
                  errorMessage={emailError}
                  errorTooltipProps={{
                    color: "error",
                    show: true,
                  }}
                  id={`${section.id}-email-text-field`}
                  name="email-text-field"
                  onChange={(event) => {
                    setEmailError("");
                    setJobCreatorConfig((previous) => {
                      return {
                        ...previous,
                        email: event.target.value,
                      };
                    });
                  }}
                  placeholder={section?.EmailTextField?.Placeholder}
                  size="large"
                  value={jobCreatorConfig?.email}
                />
              </div>
            )}
            {section?.IntervalSelect && (
              <div className="flex w-full flex-col gap-2 lg:max-w-[200px]">
                <label htmlFor="frequency-select" className="text-gray-700">
                  {section?.IntervalSelect?.Label}
                </label>
                <Select
                  name="frequency-select"
                  id={`${section.id}-frequency-select`}
                  onChange={(nextValue) => {
                    setJobCreatorConfig((previous) => {
                      return {
                        ...previous,
                        interval: nextValue,
                      };
                    });
                  }}
                  options={toSelectOptions(section?.IntervalSelect).map((option: any) => {
                    return {
                      children: option?.label,
                      value: option?.value ?? option?.label,
                    };
                  })}
                  size="large"
                  value={jobCreatorConfig.interval}
                />
              </div>
            )}
          </div>
          {section?.CreateAccountSubmitButton && (
            <Button
              className="w-full"
              disabled={section?.CreateAccountSubmitButton?.Disabled || !jobCreatorConfig || createAccountLoading}
              loading={createAccountLoading}
              size="large"
            >
              {section?.CreateAccountSubmitButton?.Value}
            </Button>
          )}
        </form>
      </div>
    </Card>
  );
};

export default JobCreator;
